import { useState } from "react"
import { useTranslation } from "react-i18next"

import { ArrowRightIcon } from "@/components/icons"
import { Button as UiKitButton } from "@/components/ui-kit/button"
import { ContractsDialogSuccess } from "@/components/contracts/modals/contracts-dialog-success"
import {
  SherpaDialog,
  SherpaDialogContent,
  SherpaDialogDescription,
  SherpaDialogFooter,
  SherpaDialogHeader,
  SherpaDialogTitle,
} from "@/components/ui-kit/dialog"
import { useContractMutations } from "@/hooks/contracts/useContracts"
import { cn, formatDateExtended } from "@/lib/utils"
import type { Contract } from "@/types/contract"

type Step = "confirm" | "success"

export function ContractsRecurrentDisableDialog({
  open,
  onOpenChange,
  contract,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  contract: Contract
}) {
  const { t } = useTranslation()
  const [step, setStep] = useState<Step>("confirm")

  const { disableRecurrent } = useContractMutations()

  const handleDisableRecurrent = () => {
    disableRecurrent.mutate(contract.id, {
      onSuccess: () => setStep("success"),
    })
  }

  const handleOpenChange = (nextOpen: boolean) => {
    // Closed mid-request, the dialog would reopen on the success step, or let the request go twice.
    if (!nextOpen && disableRecurrent.isPending) {
      return
    }
    if (!nextOpen) {
      setStep("confirm")
    }
    onOpenChange(nextOpen)
  }

  const paidUntil = contract.paidUntil ? formatDateExtended(contract.paidUntil) : ""
  const unit = contract.unitItem?.code ?? contract.unit

  return (
    <SherpaDialog open={open} onOpenChange={handleOpenChange}>
      <SherpaDialogContent
        showCloseButton={step === "confirm"}
        className={cn(
          "max-sm:left-0 max-sm:h-auto max-sm:max-h-[90dvh] max-sm:w-full max-sm:max-w-none max-sm:translate-x-0 max-sm:[--sherpa-dialog-height:auto]",
          step === "success" && "pt-8 sm:pt-10",
        )}
      >
        {step === "confirm" ? (
          <>
            <SherpaDialogHeader className="gap-6 mb-2">
              <SherpaDialogTitle>{t("contracts.recurrentDisable.title")}</SherpaDialogTitle>
              <SherpaDialogDescription>
                {t("contracts.recurrentDisable.description", { paidUntil, unit })}
              </SherpaDialogDescription>
            </SherpaDialogHeader>
            <SherpaDialogFooter className="flex-col gap-4 sm:flex-col sm:justify-start pt-[10px]">
              <UiKitButton
                type="button"
                className="h-[60px] w-full"
                endIcon={<ArrowRightIcon size={24} />}
                disabled={disableRecurrent.isPending}
                onClick={handleDisableRecurrent}
              >
                {t("contracts.recurrentDisable.confirm")}
              </UiKitButton>
            </SherpaDialogFooter>
          </>
        ) : (
          <ContractsDialogSuccess
            title={t("contracts.recurrentDisable.successTitle", { unit })}
            description={t("contracts.recurrentDisable.successDescription", { paidUntil })}
            iconWrapperClassName="pt-0"
            onClose={() => handleOpenChange(false)}
          />
        )}
      </SherpaDialogContent>
    </SherpaDialog>
  )
}
